import type {
  FilterDef,
  FilterOp,
  ReportDefinition,
  UnionSourceDef,
} from '../Settings/reportDsl';

export type IngestDeriveConfidence = 'high' | 'partial' | 'low';

export interface AppliedFilterNote {
  field: string;
  op: FilterOp;
  value?: string | number | boolean | Array<string | number>;
  how: string;
}

export interface UnionBranchPayload {
  index: number;
  label: string;
  examplePayload: Record<string, unknown>;
  appliedFilters: AppliedFilterNote[];
  caveats: string[];
}

export interface DeriveIngestPayloadResult {
  examplePayload: Record<string, unknown>;
  appliedFilters: AppliedFilterNote[];
  caveats: string[];
  confidence: IngestDeriveConfidence;
  /** When the report uses UNION ALL, one payload per source (first is also examplePayload). */
  unionExamples: UnionBranchPayload[];
}

const TOP_LEVEL_STRING_FIELDS = new Set([
  'event_type',
  'event_subtype',
  'language_iso_code',
  'user_id',
  'anonymous_id',
  'session_id',
  'page_url',
  'country_code',
  'device_type',
  'device_os',
  'utm_source',
]);

const TOP_LEVEL_NUMBER_FIELDS = new Set([
  'latitude',
  'longitude',
  'location_accuracy',
]);

const INVERTIBLE_EXACT: ReadonlySet<FilterOp> = new Set(['eq']);
const INVERTIBLE_PICK_FIRST: ReadonlySet<FilterOp> = new Set(['in']);
const SOFT_OPS: ReadonlySet<FilterOp> = new Set([
  'exists',
  'not_exists',
  'starts_with',
  'contains',
  'array_starts_with',
  'csv_any_starts_with',
]);
const CONSTRAINT_ONLY_OPS: ReadonlySet<FilterOp> = new Set([
  'neq',
  'nin',
  'not_contains',
  'not_starts_with',
  'gt',
  'gte',
  'lt',
  'lte',
]);

const buildBaselinePayload = (): Record<string, unknown> => ({
  event_type: 'page_view',
  event_subtype: null,
  language_iso_code: 'en',
  user_id: '',
  anonymous_id: 'anon-example-uuid',
  session_id: 'session-example-uuid',
  page_url: 'https://example.com/about',
  country_code: 'US',
  device_type: 'desktop',
  device_os: 'macOS',
  utm_source: null,
  custom_dimensions: {} as Record<string, string>,
  custom_metrics: {} as Record<string, number>,
  latitude: 0,
  longitude: 0,
  location_accuracy: 0,
});

const ensureDimMap = (payload: Record<string, unknown>): Record<string, string> => {
  if (!payload.custom_dimensions || typeof payload.custom_dimensions !== 'object') {
    payload.custom_dimensions = {};
  }
  return payload.custom_dimensions as Record<string, string>;
};

const ensureMetricMap = (payload: Record<string, unknown>): Record<string, number> => {
  if (!payload.custom_metrics || typeof payload.custom_metrics !== 'object') {
    payload.custom_metrics = {};
  }
  return payload.custom_metrics as Record<string, number>;
};

const firstArrayValue = (
  value: string | number | boolean | Array<string | number> | undefined,
): string | number | boolean | undefined => {
  if (Array.isArray(value)) return value.length > 0 ? value[0] : undefined;
  return value;
};

const asString = (v: string | number | boolean | undefined): string => {
  if (v === undefined || v === null) return '';
  return String(v);
};

const asNumber = (v: string | number | boolean | undefined): number => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const setPayloadField = (
  payload: Record<string, unknown>,
  field: string,
  value: string | number | boolean,
  applied: AppliedFilterNote[],
  how: string,
  op: FilterOp,
  rawValue?: string | number | boolean | Array<string | number>,
): void => {
  if (field.startsWith('custom_dimensions.')) {
    const key = field.slice('custom_dimensions.'.length);
    if (!/^[a-zA-Z0-9_\-]+$/.test(key)) return;
    ensureDimMap(payload)[key] = asString(value);
    applied.push({ field, op, value: rawValue, how });
    return;
  }
  if (field.startsWith('custom_metrics.')) {
    const key = field.slice('custom_metrics.'.length);
    if (!/^[a-zA-Z0-9_\-]+$/.test(key)) return;
    ensureMetricMap(payload)[key] = asNumber(value);
    applied.push({ field, op, value: rawValue, how });
    return;
  }
  // Materialized columns on website_events are sourced from custom_dimensions.
  if (field === 'asset_id' || field === 'search_term') {
    ensureDimMap(payload)[field] = asString(value);
    applied.push({ field, op, value: rawValue, how: `${how} (via custom_dimensions.${field})` });
    return;
  }
  if (TOP_LEVEL_STRING_FIELDS.has(field)) {
    payload[field] = field === 'event_subtype' || field === 'utm_source'
      ? (value === null || value === '' ? null : asString(value))
      : asString(value);
    applied.push({ field, op, value: rawValue, how });
    return;
  }
  if (TOP_LEVEL_NUMBER_FIELDS.has(field)) {
    payload[field] = asNumber(value);
    applied.push({ field, op, value: rawValue, how });
  }
};

const applyFiltersToPayload = (
  filters: FilterDef[] | undefined,
  payload: Record<string, unknown>,
): { applied: AppliedFilterNote[]; caveats: string[]; hasEventTypeEq: boolean } => {
  const applied: AppliedFilterNote[] = [];
  const caveats: string[] = [];
  let hasEventTypeEq = false;

  for (const f of filters || []) {
    if (!f?.field || !f.op) continue;
    const { field, op } = f;

    if (INVERTIBLE_EXACT.has(op)) {
      if (f.value === undefined || f.value === null || Array.isArray(f.value)) {
        caveats.push(`Filter ${field} ${op} has no scalar value — skipped.`);
        continue;
      }
      setPayloadField(payload, field, f.value, applied, 'eq → exact value', op, f.value);
      if (field === 'event_type') hasEventTypeEq = true;
      continue;
    }

    if (INVERTIBLE_PICK_FIRST.has(op)) {
      const first = firstArrayValue(f.value);
      if (first === undefined) {
        caveats.push(`Filter ${field} in [] is empty — skipped.`);
        continue;
      }
      setPayloadField(
        payload,
        field,
        first,
        applied,
        'in → using first listed value (other values also match the panel)',
        op,
        f.value,
      );
      if (field === 'event_type') hasEventTypeEq = true;
      const extras = Array.isArray(f.value) ? f.value.slice(1) : [];
      if (extras.length > 0) {
        caveats.push(
          `Filter ${field} in [${(f.value as Array<string | number>).join(', ')}]: example uses "${first}"; other listed values also count toward this panel.`,
        );
      }
      continue;
    }

    if (op === 'exists') {
      if (field.startsWith('custom_dimensions.') || field === 'asset_id' || field === 'search_term') {
        const key = field.startsWith('custom_dimensions.')
          ? field.slice('custom_dimensions.'.length)
          : field;
        ensureDimMap(payload)[key] = ensureDimMap(payload)[key] || `example-${key}`;
        applied.push({ field, op, how: 'exists → non-empty placeholder string' });
      } else if (field.startsWith('custom_metrics.')) {
        const key = field.slice('custom_metrics.'.length);
        if (ensureMetricMap(payload)[key] === undefined) ensureMetricMap(payload)[key] = 1;
        applied.push({ field, op, how: 'exists → placeholder numeric 1' });
      } else if (TOP_LEVEL_STRING_FIELDS.has(field)) {
        if (!payload[field] || payload[field] === '') {
          payload[field] = field === 'event_type' ? 'example_event' : `example-${field}`;
        }
        applied.push({ field, op, how: 'exists → non-empty placeholder' });
        if (field === 'event_type') hasEventTypeEq = true;
      } else {
        caveats.push(`Filter ${field} exists — ensure the field is present and non-empty in your event.`);
      }
      continue;
    }

    if (op === 'not_exists') {
      if (field.startsWith('custom_dimensions.') || field === 'asset_id' || field === 'search_term') {
        const key = field.startsWith('custom_dimensions.')
          ? field.slice('custom_dimensions.'.length)
          : field;
        delete ensureDimMap(payload)[key];
        applied.push({ field, op, how: 'not_exists → omit custom_dimensions key' });
      } else if (field.startsWith('custom_metrics.')) {
        const key = field.slice('custom_metrics.'.length);
        delete ensureMetricMap(payload)[key];
        applied.push({ field, op, how: 'not_exists → omit custom_metrics key' });
      } else if (TOP_LEVEL_STRING_FIELDS.has(field)) {
        payload[field] = field === 'event_subtype' || field === 'utm_source' ? null : '';
        applied.push({ field, op, how: 'not_exists → empty / null' });
      } else {
        caveats.push(`Filter ${field} not_exists — leave this field empty or omit it.`);
      }
      continue;
    }

    if (SOFT_OPS.has(op)) {
      const v = firstArrayValue(f.value);
      if (v === undefined || v === null) {
        caveats.push(`Filter ${field} ${op} has no value — cannot pre-fill; satisfy the ${op} constraint yourself.`);
        continue;
      }
      setPayloadField(
        payload,
        field,
        v,
        applied,
        `${op} → example uses filter value as a matching seed (not unique)`,
        op,
        f.value,
      );
      caveats.push(
        `Filter ${field} ${op} "${v}" is only a soft match — adjust the value so it still satisfies the panel filter.`,
      );
      if (field === 'event_type' && (op === 'starts_with' || op === 'contains')) {
        hasEventTypeEq = true;
      }
      continue;
    }

    if (CONSTRAINT_ONLY_OPS.has(op)) {
      caveats.push(
        `Filter ${field} ${op} ${JSON.stringify(f.value ?? '')} cannot be inverted to a unique payload value — your event must still satisfy this constraint.`,
      );
      continue;
    }

    caveats.push(`Filter ${field} ${op} is unsupported for payload derivation.`);
  }

  return { applied, caveats, hasEventTypeEq };
};

const suggestDimensionPlaceholders = (
  report: ReportDefinition,
  payload: Record<string, unknown>,
  caveats: string[],
): void => {
  const dims = report.query?.dimensions || [];
  for (const d of dims) {
    const field = d.field;
    if (!field || field === 'event_time' || field === 'event_date') continue;
    if (field.startsWith('custom_dimensions.')) {
      const key = field.slice('custom_dimensions.'.length);
      if (!ensureDimMap(payload)[key]) {
        ensureDimMap(payload)[key] = `example-${key}`;
        caveats.push(
          `Dimension ${field} had no equality filter — added placeholder custom_dimensions.${key}; replace with a real value for meaningful breakdowns.`,
        );
      }
    } else if (field.startsWith('custom_metrics.')) {
      const key = field.slice('custom_metrics.'.length);
      if (ensureMetricMap(payload)[key] === undefined) {
        ensureMetricMap(payload)[key] = 1;
        caveats.push(
          `Dimension/metric field ${field} had no equality filter — added placeholder; replace as needed.`,
        );
      }
    } else if (TOP_LEVEL_STRING_FIELDS.has(field) && (!payload[field] || payload[field] === '')) {
      // Keep baseline for common identity fields; only note for event_type/device_* style dims.
      if (field === 'event_type' || field === 'device_type' || field === 'device_os' || field === 'country_code') {
        caveats.push(
          `Dimension ${field} is grouped without an equality filter — baseline example value "${payload[field]}" may or may not match your intended series.`,
        );
      }
    }
  }
};

const deriveFromFilterList = (
  filters: FilterDef[] | undefined,
  report: ReportDefinition,
  includeDimensionHints: boolean,
): {
  examplePayload: Record<string, unknown>;
  appliedFilters: AppliedFilterNote[];
  caveats: string[];
  hasEventTypeEq: boolean;
} => {
  const payload = buildBaselinePayload();
  const { applied, caveats, hasEventTypeEq } = applyFiltersToPayload(filters, payload);
  if (includeDimensionHints) {
    suggestDimensionPlaceholders(report, payload, caveats);
  }

  const metrics = report.query?.metrics || [];
  if (metrics.length > 0) {
    caveats.push(
      'Panel metrics (count / uniqExact / sum / …) are aggregations over matching events — they are not fields you send in the ingest body. Sending events that pass the filters above will contribute to those metrics.',
    );
  }

  if (report.query?.timeRange?.preset || report.query?.timeRange?.start) {
    caveats.push(
      'Panel time range is applied at query time on event_time. Newly ingested events use the server clock (now) and will appear when the panel range includes "now".',
    );
  }

  // Drop empty maps for cleaner JSON examples when unused.
  const dims = ensureDimMap(payload);
  const mets = ensureMetricMap(payload);
  if (Object.keys(dims).length === 0) payload.custom_dimensions = {};
  if (Object.keys(mets).length === 0) payload.custom_metrics = {};

  return {
    examplePayload: payload,
    appliedFilters: applied,
    caveats,
    hasEventTypeEq,
  };
};

const computeConfidence = (
  hasEventTypeEq: boolean,
  caveats: string[],
  appliedCount: number,
  hardConstraintOnly: boolean,
): IngestDeriveConfidence => {
  if (hardConstraintOnly && appliedCount === 0) return 'low';
  if (hasEventTypeEq && caveats.filter((c) => c.includes('cannot be inverted')).length === 0) {
    return 'high';
  }
  if (hasEventTypeEq || appliedCount > 0) return 'partial';
  return 'low';
};

export interface DeriveIngestPayloadOptions {
  report: ReportDefinition;
  runtimeFilters?: FilterDef[];
}

/**
 * Best-effort inverse of Report DSL filters → an ingest AnalyticsEvent example
 * that would contribute data to the panel. Non-invertible ops become caveats.
 */
export const deriveIngestPayloadFromReportDsl = (
  opts: DeriveIngestPayloadOptions,
): DeriveIngestPayloadResult => {
  const { report, runtimeFilters } = opts;
  const union = report.query?.union;

  if (Array.isArray(union) && union.length > 0) {
    const unionExamples: UnionBranchPayload[] = union.map((src: UnionSourceDef, index: number) => {
      // Outer filters + runtime still apply after UNION on the server for many
      // reports; include them so the example stays as close as possible.
      const filters = [
        ...(src.filters || []),
        ...(report.query?.filters || []),
        ...(runtimeFilters || []),
      ];
      const derived = deriveFromFilterList(filters, report, index === 0);
      const label = `Union source ${index + 1}`;
      return {
        index,
        label,
        examplePayload: derived.examplePayload,
        appliedFilters: derived.appliedFilters,
        caveats: [
          ...derived.caveats,
          `Union branch ${index + 1}: events matching this source's filters contribute to the UNION ALL result set.`,
        ],
      };
    });

    const allCaveats = [
      `This panel uses UNION ALL with ${union.length} source(s). The primary Example Value is branch 1; additional branch payloads are listed below.`,
      ...unionExamples.flatMap((u) => u.caveats),
    ];
    const hasEventTypeEq = unionExamples.every((u) =>
      u.appliedFilters.some((a) => a.field === 'event_type' && (a.op === 'eq' || a.op === 'in')),
    );
    const appliedFilters = unionExamples[0]?.appliedFilters || [];
    const confidence = computeConfidence(
      hasEventTypeEq,
      allCaveats,
      appliedFilters.length,
      allCaveats.some((c) => c.includes('cannot be inverted')),
    );

    return {
      examplePayload: unionExamples[0]?.examplePayload || buildBaselinePayload(),
      appliedFilters,
      caveats: Array.from(new Set(allCaveats)),
      confidence,
      unionExamples,
    };
  }

  const filters = [
    ...(report.query?.filters || []),
    ...(runtimeFilters || []),
  ];
  const derived = deriveFromFilterList(filters, report, true);

  if (!derived.hasEventTypeEq) {
    derived.caveats.unshift(
      'No equality filter on event_type — the baseline event_type in the example may not match this panel. Prefer adding event_type = \'…\' to the report DSL, or edit the example before calling the API.',
    );
  }

  const confidence = computeConfidence(
    derived.hasEventTypeEq,
    derived.caveats,
    derived.appliedFilters.length,
    derived.caveats.some((c) => c.includes('cannot be inverted')),
  );

  return {
    examplePayload: derived.examplePayload,
    appliedFilters: derived.appliedFilters,
    caveats: Array.from(new Set(derived.caveats)),
    confidence,
    unionExamples: [],
  };
};
